<template>
  <div class="node-wrapper">

    <div class="node-menu-bar" >
      <div class="node-menu-item">
        <BotActionButton class="gray shadow icon" v-if="canMoveUp" @click="moveNodeUp">
          <IconArrowLeft />
        </BotActionButton>
      </div>
      <div class="node-menu-item">
        <BotActionButton class="gray shadow" v-if="canDelete" @click="deleteNode">Delete Node</BotActionButton>
      </div>
      <div class="node-menu-item">
        <BotActionButton class="gray shadow icon" v-if="canMoveDown" @click="moveNodeDown">
          <IconArrowRight />
        </BotActionButton>
      </div>
    </div>

    <div class="node">
      <div class="node-section">
        <template v-if="node.attachments && node.attachments.length">
          <div v-for="(attachment, attachmentIndex) in node.attachments" :key="attachment.key" class="attachment">
            <media :value="attachment"></media>
            <div class="node-section actions" >
              <NodeAction @deleteAction="onMediaDelete(attachmentIndex)">
                <span>
                  <IconPaperClip></IconPaperClip>
                  {{ attachment.name }} - <small>{{ mediaSize(attachment.size) }}</small>
                </span>
              </NodeAction>
            </div>
          </div>
        </template>
        <div :style="{ position: 'relative' }">
          <media-upload-btn
            v-if="!node.attachments || node.attachments.length === 0"
            @onUploaded="onMediaUploaded"
            position="absolute"
            top="7px"
            right="3px"
          >
            <IconPaperClip :style="{ width: '20px', height: '20px' }"></IconPaperClip>
          </media-upload-btn>
          <textarea v-model.trim="node.text" rows="5" class="node-text" placeholder="Message text" />
        </div>
      </div>

      <div class="node-section action-block request-user-info-container" v-if="showRequestUserInfo">
        <RequestUserInfo :requestUserInfo="node.requestUserInfo" @messageRemoveRUI="deleteRequestUserInfo" />
      </div>

      <div class="node-section actions request-user-location-container" v-if="showRequestLocation" >
        <RequestLocation :requestLocation="node.requestLocation" @messageRemoveRequestLocation="deleteRequestLocation"/>
      </div>
      
      <div class="node-section action-block bot-inline-keyboard" v-if="showInlineKeyboard">
        <div class="header">Inline Keyboard Buttons</div>
        <InlineKeyboardButton v-for="(item, index) in node.inlineKeyboard" 
          :key="index" :item="item" :index="index"
          @deleteItem="deleteInlineKeyboardButton" />
      </div>

      <div class="node-section action-block bot-reply-keyboard" v-if="showReplyKeyboard" >
        <div class="header">Reply Keyboard Buttons</div>
        <ReplyKeyboardButton v-for="(item, index) in node.replyKeyboard"
          :key="index" :item="item" :index="index"
          @deleteItem="deleteReplyKeyboardButton" />
      </div>

      <div class="node-section actions" >
        <NodeAction v-show="hasPostToQCRM" @deleteAction="setPostToQCRMStatus(false)">
          Send Dialogue Data To QCRM
        </NodeAction>
      </div>

      <div class="node-section actions" >
        <NodeAction v-show="typeof node.showNextNode !== 'undefined'" @deleteAction="deleteShowNextNode">
          Show Next Node in (ms)
          <basic-input v-model.number="node.showNextNode" placeholder="ms" class="input-timer"></basic-input>
        </NodeAction>
      </div>

      <div class="node-section actions actions-container-open" v-show="!isActionsContainerOpen">
        <BotActionButton class="gray shadow select-new-action" @click="toggleActionsContainer">
          Select new action
        </BotActionButton>
      </div>

      <div class="node-section actions-container" v-show="isActionsContainerOpen">
        <BotActionButton class="gray wide" v-if="canAddReplyKeyboard" @click="addReplyKeyboardButton">Add Reply Keyboard Button</BotActionButton>
        <BotActionButton class="gray wide" v-if="canAddInlineKeyboard" @click="addInlineKeyboardButton">Add Inline Keyboard Button</BotActionButton>
        <BotActionButton class="gray wide" v-if="canPostToQCRM" @click="setPostToQCRMStatus(true)">Post to QCRM</BotActionButton>
        <BotActionButton class="gray wide" v-if="canAddRequestUserInfo" @click="addRequestUserInfo()">Request User Info</BotActionButton>
        <BotActionButton class="gray wide" v-if="canAddRequestLocation" @click="addRequestLocation()">Request Location</BotActionButton>
        <BotActionButton class="gray wide" v-if="canAddShowNextNode" @click="addNextNodeTimer">Show next Node</BotActionButton>
      </div>

    </div>

  </div>
</template>

<script>
import helpers from '@/helpers';
import NodeAction from '@/components/app/botComponents/NodeAction';
import BotActionButton from '@/components/buttons/BotActionButton';
import InlineKeyboardButton from '@/components/app/botComponents/InlineKeyboardButton';
import ReplyKeyboardButton from '@/components/app/botComponents/ReplyKeyboardButton';
import RequestUserInfo from '@/components/app/botComponents/RequestUserInfo';
import RequestLocation from "@/components/app/botComponents/RequestLocation";
import IconArrowLeft from '@/components/icons/IconArrowLeft';
import IconArrowRight from '@/components/icons/IconArrowRight';
import BasicInput from '@/components/inputs/BasicInput'
import MediaUploadBtn from '@/components/buttons/MediaUploadBtn'
import IconPaperClip from '@/components/icons/IconPaperClip'
import Media from '@/components/app/general/Media'
import MediaMixins from '@/mixins/media.mixins'

export default {
  data: () => {
    return {
      menuOpen: false,
      actionsContainerOpen: false,
      replyKeyboardOpen: false
    }
  },
  props: ['node', 'index', 'nodesCount'],
  mixins: [MediaMixins],
  computed: {
    showInlineKeyboard() {
      return (this.node.inlineKeyboard && this.node.inlineKeyboard.length && !this.node.requestUserInfo);
    },
    showReplyKeyboard() {
      return (this.node.replyKeyboard && this.node.replyKeyboard.length && !this.node.requestUserInfo);
    },
    showRequestUserInfo() {
      return this.node.requestUserInfo;
    },
    canDelete() {
      return this.index !== 0;
    },
    canPostToQCRM() {
      return !this.node.postToQCRM;
    },
    hasPostToQCRM() {
      return this.node.postToQCRM;
    },
    isActionsContainerOpen() {
      return this.actionsContainerOpen;
    },
    canAddInlineKeyboard() {
      return !(this.node.replyKeyboard && this.node.replyKeyboard.length) && !this.node.requestUserInfo;
    }, 
    canAddReplyKeyboard() {
      return !(this.node.inlineKeyboard && this.node.inlineKeyboard.length) && !this.node.requestUserInfo;
    },
    canAddRequestUserInfo() {
      return !this.node.requestUserInfo && !(this.node.inlineKeyboard && this.node.inlineKeyboard.length) && !(this.node.replyKeyboard && this.node.replyKeyboard.length);
    },
    canMoveUp() {
      return this.index !== 0;
    },
    canMoveDown() {
      return this.index !== this.nodesCount-1;
    },
    showRequestLocation() {
      return this.node.requestLocation;
    },
    canAddRequestLocation() {
      return !this.node.requestLocation;
    },
    canAddShowNextNode() {
      return typeof this.node.showNextNode === 'undefined' && this.index + 1 < this.nodesCount;
    },
  },
  mounted() {

    this.$root.$on('closeAll', () => {
      this.setMenu(false)
      this.setActionContainer(false)
    })

  },
  methods: {
    addReplyKeyboardButton() {
      if (!this.node.replyKeyboard)
        this.$set(this.node, 'replyKeyboard', [])
      this.node.replyKeyboard.push({text: "", callback_data: ""})  
    },
    addInlineKeyboardButton() {
      if (!this.node.inlineKeyboard)
        this.$set(this.node, 'inlineKeyboard', [])
      this.node.inlineKeyboard.push({text: "", callback_data: ""})  
    },
    addRequestUserInfo() {
      if (!this.node.requestUserInfo) {
        this.$set(this.node, 'requestUserInfo', {
          requestValue: "phone",
          skipIfAlreadyProvided: true,
          validMessage: "",
          invalidMessage: "",
          telegramRequestPhoneButtonText: ""
        });
      } 
    },
    addRequestLocation() {
      if (!this.node.requestLocation) {
        this.$set(this.node, 'requestLocation', {
          buttonText: 'Share Location'
        });
      }
    },
    addNextNodeTimer() {
      if (!this.node.showNextNode) {
        this.$set(this.node, 'showNextNode', 1000);
      }
    },
    deleteRequestUserInfo() {
      this.node.requestUserInfo = undefined;
    },
    deleteRequestLocation() {
      this.node.requestLocation = undefined;
    },
    deleteReplyKeyboardButton(index) {
      this.node.replyKeyboard.splice(index, 1)
    },
    deleteInlineKeyboardButton(index) {
      this.node.inlineKeyboard.splice(index, 1)
    },
    deleteNode() {
      this.$emit('deleteNode', this.index);
    },
    moveNodeUp() {
      this.$emit('moveNodeUp', this.index);
    },
    moveNodeDown() {
      this.$emit('moveNodeDown', this.index);
    },
    toggleActionsContainer() {
      this.$root.$emit('closeAll');
      this.actionsContainerOpen = !this.actionsContainerOpen;
    },
    setActionContainer(value) {
      this.actionsContainerOpen = value;
    },
    setMenu(value) {
      this.menuOpen = value;
    },
    setPostToQCRMStatus(value) {
      this.$set(this.node, 'postToQCRM', value);
    },
    deleteShowNextNode(){
      this.node.showNextNode = undefined
    },
    onMediaUploaded(files){
      if(!this.node.attachments){
        this.$set(this.node, 'attachments', [])
      }
      this.node.attachments = [...this.node.attachments, ...files.map(({ key, contentType, originalname: name, size }) => ({ key, type: contentType, name, size }))]
    },
    onMediaDelete(index){
      if(this.node.attachments){
        this.node.attachments.splice(index, 1)
      }
    }
  },
  components: {
    BasicInput,
    Media,
    IconPaperClip,
    MediaUploadBtn,
    BotActionButton, NodeAction, InlineKeyboardButton, ReplyKeyboardButton, RequestUserInfo, RequestLocation, IconArrowLeft, IconArrowRight}
}
</script>

<style lang="scss">
  .node-wrapper {

    width: 330px;
    border-radius: 4px; 
    background-color: rgba(#303030, 0.3);
    &:hover {
      background-color: rgba(#303030, 0.5);
    }

    .node-menu-bar {
      width: 100%;
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: center;
      justify-content: space-between;
      .node-menu-item {
        width: 100%;
        text-align: center;
        &:first-child {
          text-align: left;
        }
        &:last-child {
          text-align: right;
        }
      }
    }

    .node {
      width: 100%;
      background-color: #253F3F;
      padding: 20px 16px;
      border-radius: 4px; 
      box-shadow: 0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23);

      .node-text {
        width: 100%;
        background-color: #303030;
        border: 1px solid #555555;
        padding: 8px 10px;
        color: rgba(255,255,255,0.85);
        resize: vertical;
        height: auto;
        font-size: 13px;
        border-radius: 2px;

        &:focus {
          border-color: #FBB442;
        }
      }

      .node-section {
        margin: 8px 0;
        &:first-child {
          margin-top: 0;
        }
        &:last-child {
          margin-bottom: 0;
        }
        &.actions-container-open { 
          margin: 0;
          .bot-action {
            margin-bottom: 0;
          }
        }
        

        .header {
          font-size: 13px;
          margin-bottom: 8px;
        }

        .select-new-action {
          margin: auto 0;
          width: 100%;
        }
        .bot-action {
          margin: 4px 0;
          &.no-margins {
            margin: 0;
          }
        }

        &.action-block {
          background-color: #303030;
          border-radius: 2px;
          border: solid 1px #555555;
          padding: 8px 10px;

          .header {
            font-size: 13px;
            color: rgba(255, 255, 255, 0.85);
          }
          .action-item:first-child {
            margin-top: 0;
          }
          .action-item:last-child {
            margin-bottom: 0;
          }
        }
      }
    }

    .trash-icon {
      color: #a4a4a4;
      &:hover {
        color: #FF2C2C;
      }
    }

  }

  .input-timer{
    width: 20%;
  }

</style>